<script setup>
import { computed, ref, watch } from 'vue';
import { debounce } from '@chatwoot/utils';
import VariableUrlInput from './WootWriter/VariableUrlInput.vue';
import HeadersListInput from './HeadersListInput.vue';
import WootMessageEditor from './WootWriter/Editor.vue';
import advancedWebhooksApi from 'dashboard/api/advancedWebhooks';

const model = defineModel({
  type: Object,
  default: () => ({
    url_template: '',
    headers_template: null,
    body_template: '',
    method: 'POST',
  }),
});

const httpMethods = ['GET', 'POST', 'PUT', 'DELETE'];

const method = computed({
  get: () => model.value?.method || 'POST',
  set: value => {
    model.value = { ...model.value, method: value };
  },
});

const urlTemplate = computed({
  get: () => model.value?.url_template || '',
  set: value => {
    model.value = { ...model.value, url_template: value };
  },
});

const headers = computed({
  get: () => {
    const template = model.value?.headers_template;
    if (!template) return [];
    try {
      const parsed = JSON.parse(template);
      return parsed;
    } catch {
      return [];
    }
  },
  set: value => {
    const headersJson = value.length > 0 ? JSON.stringify(value) : null;
    model.value = { ...model.value, headers_template: headersJson };
  },
});

const bodyTemplate = computed({
  get: () => model.value?.body_template || '',
  set: value => {
    model.value = { ...model.value, body_template: value };
  },
});

const showPreview = ref(false);
const previewLoading = ref(false);
const previewError = ref(null);
const previewData = ref(null);

const fetchPreview = async () => {
  previewLoading.value = true;
  previewError.value = null;

  try {
    const webhookParams = {
      url_template: urlTemplate.value,
      headers_template:
        headers.value.length > 0 ? JSON.stringify(headers.value) : null,
      body_template: bodyTemplate.value,
      method: method.value,
    };
    const response = await advancedWebhooksApi.preview(webhookParams);
    previewData.value = response.data;
  } catch (error) {
    previewError.value = error.message || 'Failed to generate preview';
  } finally {
    previewLoading.value = false;
  }
};

const togglePreview = async () => {
  showPreview.value = !showPreview.value;
  if (showPreview.value) {
    await fetchPreview();
  }
};

// Refresh preview when templates change while preview is open
const debouncedFetchPreview = debounce(() => {
  if (showPreview.value) {
    fetchPreview();
  }
}, 500);

watch(
  () => model.value,
  () => {
    debouncedFetchPreview();
  },
  { deep: true }
);
</script>

<template>
  <div class="space-y-4 mt-2">
    <div>
      <label class="mb-1 text-sm font-medium text-n-slate-12">
        {{ $t('AUTOMATION.ACTION.ADVANCED_WEBHOOK.URL_LABEL') }}
      </label>
      <div class="flex gap-2 items-center">
        <select
          v-model="method"
          class="px-2 py-1.5 bg-n-alpha-black2 border border-n-weak rounded text-sm font-mono text-n-slate-12 focus:outline-none focus:border-n-blue-6 w-20 shrink-0"
        >
          <option v-for="m in httpMethods" :key="m" :value="m">{{ m }}</option>
        </select>
        <div class="flex-1">
          <VariableUrlInput
            v-model="urlTemplate"
            type="url"
            class="answer--text-input w-full"
            :placeholder="
              $t('AUTOMATION.ACTION.ADVANCED_WEBHOOK.URL_PLACEHOLDER')
            "
          />
        </div>
      </div>
    </div>

    <HeadersListInput v-model="headers" />

    <div>
      <label class="mb-1 text-sm font-medium text-n-slate-12">
        {{ $t('AUTOMATION.ACTION.ADVANCED_WEBHOOK.BODY_LABEL') }}
      </label>
      <div dir="ltr" class="w-full max-w-full">
        <WootMessageEditor
          v-model="bodyTemplate"
          enable-variables
          :placeholder="
            $t('AUTOMATION.ACTION.ADVANCED_WEBHOOK.BODY_PLACEHOLDER')
          "
          class="action-message webhook-body-editor"
        />
      </div>
    </div>

    <div class="border-t border-n-weak pt-4" dir="ltr">
      <button
        type="button"
        class="flex items-center gap-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
        @click="togglePreview"
      >
        <span class="text-base">{{ showPreview ? '▼' : '▶' }}</span>
        <span>{{
          $t('AUTOMATION.ACTION.ADVANCED_WEBHOOK.PREVIEW.TOGGLE')
        }}</span>
      </button>

      <div v-if="showPreview" class="mt-4 space-y-4">
        <div v-if="previewLoading" class="flex items-center justify-center p-4">
          <span class="text-sm text-n-slate-10">{{
            $t('AUTOMATION.ACTION.ADVANCED_WEBHOOK.PREVIEW.LOADING')
          }}</span>
        </div>

        <div v-else-if="previewError" class="text-red-500 text-sm p-4">
          {{ previewError }}
        </div>

        <div
          v-else-if="previewData"
          class="bg-n-alpha-black2 rounded-lg p-4 border border-n-weak"
        >
          <div class="mb-3">
            <label
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
            >
              {{ $t('AUTOMATION.ACTION.ADVANCED_WEBHOOK.PREVIEW.METHOD_URL') }}
            </label>
            <div class="mt-1 flex items-center gap-2">
              <span
                class="px-2 py-1 bg-n-blue-9 text-white text-xs font-mono rounded"
              >
                {{ method }}
              </span>
              <code class="text-sm text-n-slate-12 break-all font-mono">
                {{
                  previewData.url ||
                  $t('AUTOMATION.ACTION.ADVANCED_WEBHOOK.PREVIEW.EMPTY')
                }}
              </code>
            </div>
          </div>

          <div class="mb-3">
            <label
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-2 block"
            >
              {{ $t('AUTOMATION.ACTION.ADVANCED_WEBHOOK.PREVIEW.HEADERS') }}
            </label>
            <div class="bg-n-background rounded p-3 font-mono text-xs">
              <div
                v-for="(value, key) in previewData.headers"
                :key="key"
                class="mb-1 last:mb-0"
              >
                <span class="text-n-slate-11">{{ key }}:</span>
                <span class="text-n-slate-12 ml-2">{{ value }}</span>
              </div>
            </div>
          </div>

          <div>
            <label
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-2 block"
            >
              {{ $t('AUTOMATION.ACTION.ADVANCED_WEBHOOK.PREVIEW.BODY') }}
            </label>
            <pre
              class="bg-n-background rounded p-3 font-mono text-xs text-n-slate-12 overflow-x-auto"
              >{{ previewData.body }}</pre
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.action-message {
  :deep(.ProseMirror-menubar-wrapper) {
    .ProseMirror {
      @apply bg-n-alpha-black2 rounded-lg outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6;
    }
  }
}

.webhook-body-editor {
  :deep(.ProseMirror-menubar-wrapper) {
    @apply w-full max-w-full;

    .ProseMirror {
      @apply w-full max-w-full;
      direction: ltr;
      text-align: left;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
      overflow-x: hidden;
    }
  }
}
</style>
